// ============================================================================
// Pré-remplissage du formulaire depuis l'URL.
//
//   Usage : on envoie à un prospect déjà connu un lien du type
//     /mc?prenom=Camille&email=camille@exemple.fr&tel=0612345678
//   L'étape contact arrive remplie, il n'a plus qu'à confirmer.
//
//   ⚠️ CES PARAMÈTRES SONT DES DONNÉES PERSONNELLES DANS UNE URL.
//   Laissés en place, ils partiraient vers Meta, Google, OpenAI et PostHog —
//   tous reçoivent l'URL de la page (`source_url`, `$current_url`). On les
//   déplace donc en sessionStorage et on les EFFACE de la barre d'adresse
//   immédiatement, avant qu'aucun pixel n'ait lu l'URL. D'où l'appel en phase
//   de rendu de <Providers/> et non dans un effet : les effets s'exécutent
//   après, et les effets enfants avant ceux du parent.
// ============================================================================

export const PREFILL_STORAGE_KEY = "lead_prefill";

export type LeadPrefill = {
  nomComplet?: string;
  email?: string;
  phone?: string;
};

/** Bornes de longueur : ces valeurs finissent dans des champs de formulaire. */
const clean = (v: string | null, max: number) => {
  const t = (v ?? "").trim().slice(0, max);
  return t || undefined;
};

let captured = false;

/**
 * Lit les paramètres, les met de côté, puis nettoie l'URL.
 * Idempotent : le double rendu de React en mode strict ne la rejoue pas.
 */
export function capturePrefill(): void {
  if (typeof window === "undefined" || captured) return;
  captured = true;

  const url = new URL(window.location.href);
  const q = url.searchParams;

  // `nom` est le nom complet ; `prenom` reste accepté (les pages de
  // confirmation l'utilisent déjà) et fait office de nom complet à défaut.
  const nomComplet = clean(q.get("nom") ?? q.get("prenom"), 80);
  const email = clean(q.get("email"), 120);
  const phone = clean(q.get("tel") ?? q.get("phone"), 25);

  if (!nomComplet && !email && !phone) return;

  try {
    const prev = read();
    window.sessionStorage.setItem(
      PREFILL_STORAGE_KEY,
      JSON.stringify({
        nomComplet: nomComplet ?? prev.nomComplet,
        email: email ?? prev.email,
        phone: phone ?? prev.phone,
      })
    );
  } catch {
    /* stockage indisponible : on continue sans pré-remplissage */
  }

  // On retire uniquement les clés personnelles : les UTM et `oppref` doivent
  // rester dans l'URL, d'autres modules les lisent ensuite.
  for (const k of PII_PARAMS) q.delete(k);
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
}

function read(): LeadPrefill {
  try {
    const raw = window.sessionStorage.getItem(PREFILL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LeadPrefill) : {};
  } catch {
    return {};
  }
}

/** Clés personnelles acceptées dans l'URL — jamais transmises à un tiers. */
export const PII_PARAMS = ["nom", "prenom", "email", "tel", "phone"];

/**
 * URL courante débarrassée des paramètres personnels.
 * Ceinture et bretelles : même si le nettoyage de la barre d'adresse échouait,
 * rien de personnel ne part dans un `source_url`.
 */
export function sanitizedHref(): string {
  if (typeof window === "undefined") return "";
  try {
    const u = new URL(window.location.href);
    for (const k of PII_PARAMS) u.searchParams.delete(k);
    return u.toString();
  } catch {
    return window.location.origin + window.location.pathname;
  }
}

/** Valeurs à injecter dans le formulaire. Vide si rien n'a été transmis. */
export function getPrefill(): LeadPrefill {
  if (typeof window === "undefined") return {};
  return read();
}
